/**
 * English translations.
 */
const en = {
  // ── App-level ─────────────────────────────────────
  app: {
    skip_to_content: "Skip to main content",
  },

  // ── Navigation ────────────────────────────────────
  nav: {
    home: "Home",
    predictions: "Races",
    championship: "Champ.",
    leagues: "Leagues",
    profile: "Profile",
    live: "Live",
    standings: "Standings",
    preds_short: "Preds",
    aria_label: "Main navigation",
  },

  // ── Auth ──────────────────────────────────────────
  auth: {
    tagline: {
      main: "Make your predictions, challenge your friends,\nlive F1 like never before.",
      footer: "Predict. Challenge.",
      footer_accent: "Live it.",
    },
    tabs: {
      login: "Login",
      register: "Sign Up",
    },
    form: {
      email_label: "Email",
      email_placeholder: "you.com",
      password_label: "Password",
      remember_me: "Remember me",
      forgot_password: "Forgot?",
    },
    login: {
      submit: "Log in",
      loading: "Logging in...",
      success: "Logged in!",
    },
    register: {
      username_label: "Driver alias",
      username_placeholder: "VerstappenFan, LeclercSZN...",
      password_placeholder: "8 characters minimum",
      nationality_label: "Nationality",
      nationality_placeholder: "Select your country",
      submit: "Create my account",
      loading: "Creating...",
      success: "Account created!",
    },
    magic_link: {
      send: "Get a magic link",
      sent: "Link sent ✓",
      verifying: "Verifying magic link...",
      email_required: "Enter your email to get a magic link",
      send_error: "Failed to send magic link",
      send_success: "Magic link sent!",
      success: "Magic link confirmed!",
      invalid: "Invalid or expired magic link",
    },
    invite: {
      label: "Invitation detected",
      description: "Sign in to join league {{code}}.",
    },
    social: {
      divider: "or continue with",
    },
    footer: {
      new_user: "New here?",
      register_link: "Join the race",
      existing_user: "Already signed up?",
      login_link: "Log in",
    },
    error: {
      generic: "Something went wrong",
    },
  },

  // ── Username setup ────────────────────────────────
  username: {
    progress: "Step 3/3",
    title: "Pick your alias",
    subtitle: "This is how your friends will see you.",
    placeholder: "SpeedyMax",
    validation: {
      char_count: "3-20 chars",
      allowed: "Letters, digits, _",
    },
    preview_badge: "New",
    submit: "Let's go!",
    loading: "Saving...",
    success: "Username saved!",
    taken: "This username is already taken",
  },

  // ── Password ──────────────────────────────────────
  password: {
    forgot: {
      title: "Forgot your password?",
      subtitle: "Enter your email to receive a reset link.",
      email_placeholder: "you.com",
      submit: "Send reset link",
      loading: "Sending...",
      success_title: "Email sent",
      success_message:
        "If an account exists for this email, you'll receive a link to reset your password.",
      resend_timer: "Resend in {{seconds}}s",
      resend: "Resend email",
      open_mailbox: "Open my mailbox",
      back: "Back to login",
      error: "Something went wrong. Try again.",
    },
    reset: {
      title: "New password",
      subtitle: "Choose a secure password.",
      password_label: "New password",
      confirm_label: "Confirm",
      submit: "Reset password",
      loading: "Resetting...",
      success_title: "Password changed",
      success_message: "You can now log in with your new password.",
      success_login: "Log in",
      invalid_title: "Invalid link",
      request_new: "Request a new link",
      invalid_link: "Invalid reset link",
      mismatch: "Passwords don't match",
      invalid_criteria: "Password doesn't meet all requirements",
      match_ok: "Passwords match",
      match_fail: "Doesn't match",
      error: "Failed to reset password",
      rules: {
        characters: "8+ characters",
        uppercase: "1 uppercase",
        lowercase: "1 lowercase",
        digit: "1 digit",
      },
      strength: {
        weak: "Weak",
        medium: "Medium",
        good: "Good",
        strong: "Strong",
      },
    },
  },

  // ── Splash screen ────────────────────────────────
  splash: {
    skip: "Skip",
    start: "Get started",
    start_dashboard: "Enter the paddock",
    start_login: "Log in",
    start_admin: "Access back-office",
    baseline: "Predict. Challenge. Thrill.",
    loading_label: "Paddock sync",
    logs: [
      "Initializing paddock",
      "Loading 2026 calendar",
      "Preparing predictions",
      "Opening the grid",
    ],
    aria_content: "PronoKif F1 launch screen",
    aria_loading: "Loading PronoKif F1",
    aria_steps: "Loading steps",
  },

  // ── Email verification ────────────────────────────
  email_verification: {
    message: "Verify your email to secure your account.",
    resend: "Resend",
    sending: "Sending...",
    sent: "Verification email sent!",
    error: "Failed to send",
    close: "Close",
  },

  // ── Public menu content ──────────────────────────
  menu: {
    tutorial: {
      title: "Tutorial",
      subtitle: "How to play PRONOKIF",
      steps: [
        {
          title: "1. Create or join a league",
          description:
            "Tap the Leagues tab at the bottom of the screen. You can create your own league and invite friends with a code, or join an existing league.",
        },
        {
          title: "2. Make your predictions",
          description:
            "Before each Grand Prix, go to the Preds tab. Predict pole position, qualifying top 10, the winner, race top 10, and bonus picks.",
        },
        {
          title: "3. Sprint weekend",
          description:
            "During a Sprint weekend, you have two prediction sets: one for the sprint (locks before SQ1) and one for the main race (locks before Q1).",
        },
        {
          title: "4. Bonus picks",
          description:
            "Do not forget bonus picks: Safety Car (yes/no), DNF drivers, fastest lap, and first-corner leader. They can be worth a lot.",
        },
        {
          title: "5. Score points",
          description:
            "After each race, results are entered and your points are calculated automatically. Check your profile history for details.",
        },
        {
          title: "6. Mini-games",
          description:
            "Play mini-games (Reaction and Batak) to earn bonus points and challenge your friends. The best player in each league gets +2 points!",
        },
        {
          title: "7. Leaderboard",
          description:
            "Follow your league leaderboard and the global ranking. Beat your friends and become season champion.",
        },
      ],
    },
    privacy: {
      title: "Privacy",
      subtitle: "Data protection",
      collected_title: "Data collected",
      collected_intro: "PRONOKIF only collects the data needed for the app to work:",
      collected_items: [
        "Email address (for authentication)",
        "Username (for leaderboard display)",
        "Predictions and game scores",
        "League memberships",
      ],
      usage_title: "Data usage",
      usage_intro: "Your data is used exclusively to:",
      usage_items: [
        "Manage your account and predictions",
        "Calculate and display leaderboards",
        "Enable member interactions (chat, leagues)",
      ],
      security_title: "Security",
      security_intro: "Your data is protected:",
      security_items: [
        "Hashed passwords (bcrypt)",
        "Secure connection (HTTPS)",
        "No data sold to third parties",
      ],
      rights_title: "Your rights",
      rights_intro: "Under GDPR, you can:",
      rights_items: [
        "Access your personal data",
        "Request changes or deletion",
        "Export your data",
      ],
      contact_prefix: "Contact us at",
      contact_suffix: "to exercise your rights.",
    },
    legal: {
      title: "Legal notice",
      subtitle: "Legal information",
      publisher_title: "App publisher",
      publisher_text: "PRONOKIF is a free, non-commercial game app created by a Formula 1 fan.",
      contact_label: "Contact:",
      hosting_title: "Hosting",
      hosting_text: "The app is hosted on the Emergent platform.",
      ip_title: "Intellectual property",
      ip_text:
        "PRONOKIF is not affiliated with Formula 1, the FIA, or FOM. Trademarks, logos, and driver/team names belong to their respective owners.",
      ip_owner: "The app visuals and code are the property of the publisher.",
      liability_title: "Liability",
      liability_text:
        "PRONOKIF is a prediction game with no financial stakes. The publisher declines responsibility for use of the app.",
      terms_title: "Terms of use",
      terms_items: [
        "The app is free and will remain free",
        "Registration implies acceptance of the rules",
        "Inappropriate behavior may lead to exclusion",
        "The administrator reserves the right to change the rules",
      ],
    },
    contact: {
      title: "Contact",
      subtitle: "Write to us",
      intro:
        "Have a question, suggestion, or bug to report? Feel free to contact the PRONOKIF administrator.",
      email_label: "Email address",
      send_email: "Send an email",
      quick_feedback_prefix: "You can also use the",
      quick_feedback_suffix:
        "button at the top right of the screen to send quick feedback directly from the app.",
    },
    hamburger: {
      open: "Open menu",
      close: "Close menu",
      dialog: "Main menu",
      menu: "Menu",
      back: "Back",
    },
    rules: {
      title: "Game rules",
      subtitle: "How it works",
      principle_title: "General principle",
      principle_text:
        "PRONOKIF is an F1 prediction game. Before each Grand Prix, you predict qualifying and race results. The more precise your picks, the more points you score!",
      required_title: "Required predictions",
      required_items: [
        { label: "Pole Position", text: "Pick the driver who will take pole" },
        { label: "Qualifying Top 10", text: "Pick the first 10 qualifiers in order" },
        { label: "Winner", text: "Pick the race winner" },
        { label: "Race Top 10", text: "Pick the first 10 finishers in order" },
      ],
      bonus_title: "Bonus picks",
      bonus_items: [
        { label: "Safety Car", text: "Will there be a Safety Car during the race?" },
        { label: "Fastest lap", text: "Which driver will set fastest lap?" },
        { label: "Turn 1 leader", text: "Who will lead at the first corner?" },
        { label: "DNF", text: 'Which drivers will retire? (or "No DNF")' },
      ],
      sprint_title: "Sprint weekends",
      sprint_intro: "During Sprint weekends, you have",
      sprint_strong: "two prediction sets",
      sprint_suffix: "to complete:",
      sprint_items: [
        "Sprint: locks 15 min before Sprint Qualifying (SQ1)",
        "Main race: locks 15 min before Qualifying (Q1)",
      ],
      deadline_title: "Deadlines",
      deadline_intro: "Predictions must be saved",
      deadline_strong: "15 minutes before the first session starts",
      deadline_suffix: "for that set (Q1 for the race, SQ1 for the sprint).",
      deadline_warning: "After the deadline, predictions can no longer be changed!",
      leagues_title: "Leagues",
      leagues_text:
        "Create or join a league to challenge your friends! Each league has its own leaderboard. You can belong to several leagues at once.",
    },
    scoring: {
      title: "Points system",
      subtitle: "How points are calculated",
      main: [
        {
          category: "Qualifying",
          items: [
            { label: "Exact Pole Position", points: "+5 pts" },
            { label: "Driver in Top 10 (correct position)", points: "+3 pts" },
            { label: "Driver in Top 10 (wrong position)", points: "+1 pt" },
          ],
        },
        {
          category: "Race",
          items: [
            { label: "Exact winner", points: "+5 pts" },
            { label: "Driver in Top 10 (correct position)", points: "+3 pts" },
            { label: "Driver in Top 10 (wrong position)", points: "+1 pt" },
          ],
        },
      ],
      bonus_title: "Bonus picks",
      bonus: [
        { label: "Safety Car (correct answer)", points: "+3 pts" },
        { label: "Exact fastest lap", points: "+5 pts" },
        { label: "Exact first-corner leader", points: "+5 pts" },
        { label: "Correct DNF (per driver)", points: "+3 pts" },
        { label: "Correct “No DNF”", points: "+5 pts" },
      ],
      special_title: "Special bonuses",
      special: [
        { label: "Perfect qualifying top 10 (10/10 correct positions)", points: "+10 pts BONUS" },
        { label: "Perfect race top 10 (10/10 correct positions)", points: "+10 pts BONUS" },
        { label: "Best mini-game score in the league", points: "+2 pts" },
      ],
      sprint_label: "Sprint weekend:",
      sprint_text:
        "The same points system applies to Sprint predictions (Sprint Qualifying + Sprint Race + Sprint Bonus).",
      max_title: "Maximum points per GP (classic race)",
      max_rows: [
        { label: "Quali (Pole + perfect Top 10)", points: "45 pts" },
        { label: "Race (Winner + perfect Top 10)", points: "45 pts" },
        { label: "Bonus (SC + FL + T1 + DNF)", points: "~20 pts" },
      ],
      max_total: "Total max",
      max_total_points: "~110 pts",
    },
  },

  feedback: {
    categories: {
      bug: "Report a bug",
      suggestion: "Suggestion",
      feedback: "Experience",
    },
    max_length_error: "Message too long (max 2000 characters)",
    success_toast: "Message sent!",
    error_toast: "Error while sending. Try again.",
    title: "Help the administrator",
    subtitle: "Your feedback matters!",
    thanks_title: "Thanks!",
    thanks_message: "Your message has been sent to the administrator.",
    intro_prefix: "Help me improve the app! Share your",
    intro_feedback: " feedback",
    intro_bugs: " bugs",
    intro_suggestions: " suggestions",
    category_label: "Category",
    message_label: "Your message",
    placeholders: {
      bug: "Describe the issue you found...",
      suggestion: "What feature would you like to see?",
      feedback: "Share your experience...",
    },
    char_count: "{{count}}/2000 characters",
    sending: "Sending...",
    submit: "Send my message",
  },

  predictions: {
    race_not_found: "Race not found",
    back: "Back",
    saved_sprint: "Sprint saved!",
    saved_main: "Predictions saved!",
    header_subtitle: "Race prediction",
    delete_title: "Delete my predictions",
    sprint: "Sprint",
    race: "Race",
    closed_short: "Closed",
    status: {
      sprint_ready: "Sprint ready",
      sprint_incomplete: "Sprint incomplete",
      race_ready: "Race ready",
      race_incomplete: "Race incomplete",
    },
    timer: {
      deadline: "Prediction lock",
      sprint: "Closes 15 min before SQ1",
      race: "Closes 15 min before Q1",
    },
    saving: "Saving...",
    closed: "Predictions closed",
    save: "Save",
    incomplete: "Incomplete",
    form: {
      guided_path: "Guided path",
      build_ticket: "Build your ticket",
      essential: "essential",
      validated: "validated",
      active_step: "Active step",
      to_choose: "To pick",
      free_slot: "Free slot",
      quick_steps_label: "Quick steps",
      todo: "to do",
      done: "done",
      saved: "saved",
      now_selecting: "Now picking",
      driver_grid: "Driver grid",
      pick_driver: "Pick your driver",
      drivers_short: "drivers",
      continue_to_next: "Continue",
      optional: "optional",
      ticket_status: "Ticket status",
      ticket_ready: "The essential ticket is ready to save.",
      next_target: "Next target: {{label}} {{sublabel}}.",
      keep_going: "Keep going in the cockpit.",
      games: "Games",
      mini: "Mini",
      validate_back_bonus: "Confirm and back to bonuses",
      stage_qualifying: "Quali",
      stage_sprint_q: "Sprint Q",
      stage_race: "Race",
      stage_sprint: "Sprint",
      stage_bonus: "Bonus",
      stage_minigames: "Mini-games",
      steps: {
        pole: "Pole",
        top10: "Top 10",
        winner: "Winner",
        bonus: "Bonus",
        games: "Games",
        sprint_q: "Sprint Q",
        sprint: "Sprint",
        qualif: "Quali",
        race: "Race",
        bets: "Bets",
        mini: "Mini",
      },
      help: {
        pole: "Pick the driver who will start ahead of everyone.",
        winner: "Select your winner. The top 10 comes right after.",
        top10: "Build the exact order from P1 to P10. Tap again to remove a driver.",
        fastest_lap: "Pick the fastest driver over one flying lap.",
        first_corner: "Who will lead at the first corner?",
        dnf: "Select up to 5 retirements, then confirm your choice.",
        sprint_bonus: "Add Sprint bonus picks.",
        race_bonus: "Add race bonus picks.",
        default: "Move step by step; the cockpit guides you automatically.",
      },
      bonus: {
        title: "Bonus Questions",
        points: "+50 pts",
        safety_car: "Safety Car",
        yes: "Yes",
        no: "No",
        fastest_lap: "Fastest lap",
        first_corner: "Turn 1 leader",
        dnf: "DNF",
        no_dnf: "No DNF",
        selected: "Selected",
        select: "Select →",
        drivers_count: "{{count}} driver(s)",
      },
      selection: {
        sprint_quali_pole: "Select the Sprint qualifying pole-sitter",
        sprint_quali_top10: "Select the Sprint qualifying Top 10 ({{count}}/10)",
        sprint_race_winner: "Select the Sprint winner",
        sprint_race_top10: "Select the Sprint Top 10 ({{count}}/10)",
        sprint_bonus: "Configure your Sprint bonuses",
        sprint_fastest_lap: "Select the driver who will set fastest lap in the Sprint",
        sprint_first_corner: "Select the first-corner leader in the Sprint",
        sprint_dnf_select: "Select Sprint DNFs ({{count}}/5)",
        quali_pole: "Select the pole-sitter",
        quali_top10: "Select the qualifying Top 10 ({{count}}/10)",
        race_winner: "Select the race winner",
        race_top10: "Select the race Top 10 ({{count}}/10)",
        bonus: "Configure your bonuses",
        fastest_lap: "Select the driver who will set fastest lap",
        first_corner: "Select the first-corner leader",
        dnf_select: "Select DNFs ({{count}}/5)",
      },
    },
  },

  leagues_page: {
    pilot_fallback: "driver",
    onboarding_badge: "League step",
    welcome: "Welcome {{name}}",
    onboarding_text: "Last step before the paddock: create your league or join your friends.",
    onboarding_steps: ["Profile", "League", "Preds"],
    choices: {
      create_title: "Create a league",
      create_description: "Open your private paddock and invite your group.",
      join_title: "I have a code",
      join_description: "Enter an invitation code to join the grid.",
    },
    joined_toast: 'You joined "{{name}}"!',
    invalid_code: "Invalid code",
    created_toast: "League created!",
    create_error: "Error while creating",
    code_copied: "Code copied!",
    copy_error: "Unable to copy",
    share_text: 'Join my F1 league "{{name}}" on PRONOKIF!',
    selected_toast: "League selected!",
    title: "My leagues",
    join_tab: "Join",
    create_tab: "Create",
    join_intro: "Enter the code of the league you want to join",
    code_label: "Invitation code",
    joining: "Connecting...",
    join_submit: "Join league",
    create_intro: "Create your own league and invite your friends",
    name_label: "League name",
    name_placeholder: "Ex: F1 Champions",
    creating: "Creating...",
    create_submit: "Create my league",
    social_unlock_title: "Your active league unlocks social play",
    social_unlock_text:
      "Friend leaderboards, league chat, mini-games, and custom predictions will appear once this step is complete.",
  },

  search: {
    groups: {
      navigation: "Navigation",
      drivers: "Drivers",
      races: "Races",
      leagues: "Leagues",
      players: "Players",
      history: "History",
    },
    date_tbc: "Date to be confirmed",
    status: {
      finished: "Finished",
      in_progress: "Live",
      cancelled: "Cancelled",
      upcoming: "Upcoming",
    },
    static: {
      dashboard_title: "Home",
      dashboard_subtitle: "Dashboard, next Grand Prix, league activity",
      predictions_title: "Predictions",
      predictions_subtitle: "Race calendar and prediction flow",
      championship_title: "2026 Championship",
      championship_subtitle: "Drivers, teams, championship, and calendar",
      results_title: "Results",
      results_subtitle: "Scores, official results, and points earned",
      global_leaderboard_title: "Global leaderboard",
      global_leaderboard_subtitle: "Top app players",
      compare_title: "Compare two drivers",
      compare_subtitle: "Head-to-head, stats, and records",
      profile_title: "My profile",
      profile_subtitle: "Account, progress, leagues, and personal stats",
    },
    league_badge: "League",
    player_badge: "Player",
    history_badge: "History",
    league_subtitle: "{{count}} player(s) • code {{code}}",
    player_subtitle: "#{{position}} • {{points}} pts • level {{level}}",
    history_pending: "Prediction waiting for results",
  },

  search_dialog: {
    title: "Deep search",
    description: "Search drivers, races, leagues, players, and PronoKif pages.",
    eyebrow: "Deep search",
    subtitle: "Drivers, races, leagues, players, results, and navigation.",
    placeholder: "Search RUS, Monaco, Ferrari, ranking, player...",
    empty: "No results for this search.",
    indexing: "Indexing data...",
    help: "Enter to open • Esc to close",
    local_index: "Enhanced local index",
  },

  // ── Common labels ────────────────────────────────
  common: {
    pts: "pts",
    members: "members",
    players: "players",
    gp: "GP",
    xp: "XP",
    level_short: "Lvl.{{level}}",
    rank_suffix: "th",
    cancel: "Cancel",
    back: "Back",
    see_all: "See all",
    error: "Error",
    loading: "Loading...",
    n_a: "N/A",
    confirmed: "Confirmed",
    you: "(you)",
    days: "days",
    hours: "hours",
    min: "min",
    sec: "sec",
    in_time: "in",
  },

  // ── Dashboard ────────────────────────────────────
  dashboard: {
    greeting: {
      night: "Good night",
      morning: "Good morning",
      afternoon: "Good afternoon",
      evening: "Good evening",
    },
    fallback_name: "Driver",
    total_points: "Total points",
    last_gp: "+{{pts}} last GP",
    global_rank: "Global rank",
    precision: "Accuracy",
    gps_played: "{{count}} GPs played",
    current_streak: "Current streak",
    my_leagues: "My leagues",
    join: "Join",
    join_league_cta: "Join a League!",
    join_league_desc: "Create or join a league to play with your friends",
    lets_go: "Let's go!",
    add: "Add",
    share_title: "Join {{name}} on PronoKif!",
    members_count: "{{count}} members",
    help_admin: "Help the admin",
    help_admin_desc: "Report a bug or share feedback",
    recent_results: "Recent results",
    history: "History",
    no_results: "No results imported",
    quick_actions: "Quick actions",
    predict: "Predict",
    standings: "Standings",
    invite: "Invite",
    share_app: "Share app",
    share_text: "Make your F1 predictions with me!",
    live: "Live",
    no_race: "No race",
    season: "Season {{year}}",
    tier: {
      diamond: "Diamond",
      platinum: "Platinum",
      gold: "Gold",
      silver: "Silver",
      bronze: "Bronze",
      default: "Ranking",
    },
    aria_profile: "View my profile",
    aria_search: "Search",
  },

  // ── Race carousel ────────────────────────────────
  race_carousel: {
    calendar: "Calendar",
    races_to_predict: "{{count}} race(s) to predict",
    up_to_date: "You're up to date",
    calendar_unavailable: "Calendar unavailable",
    see_all: "See all",
    status: {
      to_predict: "To predict",
      predicted: "Predicted",
      finished: "Finished",
      cancelled: "Cancelled",
      closed: "Predictions closed",
    },
    aria_calendar: "Grand Prix calendar",
    aria_scroll: "Scroll calendar",
  },

  // ── Race calendar ────────────────────────────────
  race_calendar: {
    title: "Races 2026",
    subtitle: "Predict until each race starts",
    filters: {
      upcoming: "Upcoming",
      finished: "Finished",
      all: "All",
    },
    status: {
      to_predict: "To predict",
      predicted: "Predicted",
      finished: "Finished",
      cancelled: "Cancelled",
      closed: "Closed",
      live: "Live",
    },
    cta: {
      results: "Results",
      cancelled: "Cancelled",
      locked: "Locked",
      edit: "Edit",
      predict: "Predict",
      follow_live: "Follow Live",
    },
    closing_aria: "Predictions close in {{time}}",
    empty_title: "No races",
    empty_season_over: "Season finished",
    empty_none: "No races here",
    aria_race: "Race details {{name}}",
  },

  // ── Grand Prix detail ────────────────────────────
  grand_prix: {
    live_results: {
      title_live: "Live results",
      title_final: "Grand Prix results",
      awaiting: "Waiting for results…",
      podium: "Podium",
      your_score: "Your score this GP",
      pending_score: "Pending",
      points: "{{count}} pts",
      leagues: "Your leagues",
      no_leagues: "Join a league to compare",
      position: "P{{pos}}",
      see_details: "See full results",
      your_picks: "Your picks",
      pole: "Pole",
      winner: "Winner",
      corrected: "Results updated on {{date}} following an official decision.",
    },
    tabs: {
      circuit: "Circuit",
      schedule: "Schedule",
      grid: "Grid",
      picks: "Picks",
    },
    sessions: {
      fp1: "Free Practice 1",
      fp2: "Free Practice 2",
      fp3: "Free Practice 3",
      sprint_quali: "Sprint Qualifying",
      sprint: "Sprint",
      qualifying: "Qualifying",
      race: "Race",
    },
    status: {
      finished: "Race finished",
      pick_deadline: "Picks deadline",
      paris_time: "local time",
    },
    cta: {
      unavailable: "Picks unavailable",
      predict: "Make my picks",
      sprint_picks: "Sprint Picks",
      results: "See results",
    },
    picks: {
      your_picks: "Your picks",
      modify: "Edit my picks",
      modify_sprint: "Edit Sprint picks",
      locked: "Locked",
      live_hint: "Race in progress — results on the Circuit tab.",
      none_finished: "You didn't make picks for this Grand Prix.",
      qualifying: "Qualifying",
      race: "Race",
      sprint_quali: "Sprint · Qualifying",
      sprint_race: "Sprint · Race",
      pole: "Pole",
      winner: "Winner",
      top10: "Top 10",
      bonus: "Bonus",
      safety_car: "Safety Car",
      fastest_lap: "Fastest lap",
      first_corner: "Turn 1 leader",
      dnf: "DNFs",
      no_dnf: "No DNF",
      yes: "Yes",
      no: "No",
    },
    empty: {
      not_found: "Grand Prix not found",
      no_schedule: "Schedule unavailable",
      no_picks: "Picks unavailable",
    },
    deadline: {
      sprint: "Closes 15 min before SQ1",
      race: "Closes 15 min before Q1",
    },
    grid: {
      unavailable_title: "Grid unavailable",
      unavailable_desc: "The driver and team list is not yet available.",
      driver_count_one: "driver",
      driver_count_other: "drivers",
      starting_grid_title: "Starting Grid",
      pole_position: "Pole Position",
      quali_not_started_title: "Qualifying not finished",
      quali_not_started_desc: "The starting grid will be revealed once qualifying ends.",
      quali_awaiting_title: "Awaiting results",
      quali_awaiting_desc:
        "Qualifying has just ended. The starting grid will appear as soon as results are entered.",
    },
    circuit_stats: {
      turns: "turns",
      laps: "laps",
    },
    countdown_in: "{{session}} in",
    picks_open_soon: "Predictions will open soon for this Grand Prix.",
    error_loading: "Unable to load details",
    not_exist: "This Grand Prix does not exist",
  },

  // ── Profile ──────────────────────────────────────
  profile: {
    title: "My profile",
    global_rank: "Global rank:",
    total_points: "Total points",
    predictions: "Predictions",
    missions: "Missions",
    missions_sub: "Earn XP",
    custom_picks: "Custom Picks",
    custom_picks_sub: "Create custom picks",
    global_leaderboard: "Global leaderboard",
    global_leaderboard_sub: "All players",
    league_leaderboard: "League leaderboard",
    admin: "Administration",
    app_section: "Application",
    install_app: "Install app",
    install_desc: "Add PronoKif to your home screen",
    update_available: "Update available",
    update_desc: "Reload to apply the latest version",
    installed: "App installed",
    installed_desc: "PronoKif is running in native app mode",
    install_instructions: "To install PronoKif on your home screen:",
    ios_instructions: "iOS Safari: Share → Add to Home Screen",
    android_instructions: "Android Chrome: Menu → Install app",
    desktop_instructions: "Desktop: Address bar → Install",
    clear_cache: "Clear cache and reload",
    cache_cleared: "Cache cleared. Reloading...",
    logout: "Log out",
    avatar_modal: "Choose an Avatar",
    avatar_updated: "Avatar updated!",
    avatar_error: "Error updating avatar",
    photo_uploaded: "Photo uploaded!",
    photo_error: "Error uploading photo",
    install_launched: "Installation started!",
    cagnotte_title: "Kitty",
    cagnotte_desc: "Points earned from mini-games",
    cagnotte_pts: "pts",
    cagnotte_reaction: "Reaction",
    cagnotte_batak: "Batak",
    cagnotte_games_played: "{{count}} competition games",
  },

  // ── Avatar ───────────────────────────────────────
  avatar: {
    tabs: {
      generate: "Generate Avatar",
      drivers: "Drivers",
      teams: "Teams",
      classics: "Classics",
    },
    custom_alt: "Custom avatar",
    driver_alt: "Driver",
    steps: {
      photo: "Add your photo",
      compose: "Helmet & face",
      background: "Background",
    },
    photo_intro: "Start with a face photo — the helmet will be placed on top.",
    capture: "Capture",
    cancel: "Cancel",
    upload_photo: "Upload a photo",
    take_selfie: "Take a selfie",
    file_too_large: "Image too large (max 5 MB)",
    face_analysis: "Analysing face…",
    helmet_counter: "{{team}} · {{current}}/{{total}} · swipe to change helmet",
    drag_hint: "Drag / pinch to adjust the face in the visor",
    helmet_library: "Helmet library",
    choose_helmet: "Choose helmet {{team}}",
    next: "Next",
    all_teams: "All",
    bg_label: "Background",
    bg_scene: "Scene",
    bg_gradient: "Gradient",
    bg_color: "Colour",
    apply: "Apply to my profile",
    applying: "Applying…",
    download: "Download (PNG)",
    downloading: "Downloading…",
    save_error: "Error saving",
    download_error: "Error downloading",
    camera_errors: {
      https_required: "Camera requires a secure connection (HTTPS or localhost).",
      not_supported: "Camera is not supported by this browser.",
      permission_denied: "Camera access denied. Allow camera in browser settings.",
      not_found: "No camera detected on this device.",
      in_use: "Camera is already in use by another application.",
      generic: "Unable to access camera.",
      not_ready: "Camera is not ready yet — try again in a moment.",
    },
    scenes: {
      tarmac: "Tarmac",
      grid: "Grid",
      pit: "Pit",
      podium: "Podium",
      cockpit: "Cockpit",
      checkered: "Checkered",
    },
    driver_count_one: "driver",
    driver_count_other: "drivers",
  },

  // ── My leagues section ───────────────────────────
  my_leagues: {
    title: "My leagues",
    add: "Add",
    no_league: "No leagues",
    create_join: "Create / Join",
    select: "Select",
    code_copied: "Code copied!",
    copy_error: "Unable to copy",
    share_text: 'Join my F1 league "{{name}}" on PRONOKIF!',
    selected: "League selected!",
    select_error: "Error",
  },

  // ── Leaderboard ──────────────────────────────────
  leaderboard: {
    title: "Leaderboard",
    league_title: "League leaderboard",
    no_ranking: "No ranking yet",
    empty_message: "Points will be calculated after races",
    invite_friends: "Invite your friends to get started!",
    invite_cta: "Invite friends",
    no_league: "No league yet",
    join_cta: "Create or join a league",
    create_join: "Create / Join",
    last_gp: "last GP",
    copy: "Copied",
    code: "Code",
    change: "Change",
    chat: "Chat",
    league: "League",
    league_selected: 'League "{{name}}" selected',
    league_error: "Error switching league",
    code_copied: "Code copied!",
    copy_error: "Unable to copy",
    aria_share: "Share league",
    aria_chat: "League chat",
    aria_copy: "Copy code",
    aria_join: "Join a league",
  },

  // ── Global leaderboard ───────────────────────────
  global_leaderboard: {
    title: "Global leaderboard",
    filters: {
      season: "Season",
      month: "This month",
      last_gp: "Last GP",
    },
    not_enough: "Not enough players.",
  },

  // ── Championship ─────────────────────────────────
  championship: {
    title: "F1 Championship",
    season: "Season {{year}}",
    tabs: {
      drivers: "Drivers",
      teams: "Teams",
      results: "Results",
    },
    updated: "Updated: {{date}}",
    refresh_success: "Standings updated!",
    compare_title: "Compare drivers",
    attribution: "Data provided by Jolpica F1 API & OpenF1",
    season_progress: {
      select_gp: "Select a Grand Prix",
      upcoming: "Upcoming",
      loading: "Loading results...",
      tabs: {
        race: "Race",
        qualifying: "Qualifying",
        sprint: "Sprint",
        practice: "Practice",
        bonus: "Bonus",
      },
      empty: "Select a finished Grand Prix to see the results",
      load_error: "Error loading results",
    },
    drivers: {
      no_data: "No data available",
    },
    constructors: {
      no_data: "No data available",
      pts: "pts",
      wins_one: "{{count}} win",
      wins_other: "{{count}} wins",
    },
  },

  // ── Results ──────────────────────────────────────
  results: {
    title: "Results",
    score_title: "Grand Prix Score",
    qualifying: "Qualifying",
    pole: "Pole position",
    race: "Race",
    winner: "Winner",
    points_detail: "Points breakdown",
    no_results: "No results",
    pending: "Results pending",
    no_prediction: "You didn't make a prediction for this race",
  },

  // ── Notifications ────────────────────────────────
  notifications: {
    filters: {
      all: "All",
      races: "Races",
      leagues: "Leagues",
      chat: "Chat",
      badges: "Badges",
    },
    categories: {
      messages: "Messages",
      results: "Results",
      leagues: "Leagues",
      badges: "Badges",
      general: "General",
    },
    action_required: "Action required",
    predict_cta: "Make my predictions",
    empty: "No notifications",
    empty_category: "No notifications in this category.",
    mark_all_read: "Mark all as read",
    bell_label: "Notifications",
    bell_unread: "Notifications ({{count}} unread)",
    time: {
      yesterday: "yesterday",
      just_now: "just now",
    },
  },

  // ── Missions ─────────────────────────────────────
  missions: {
    tabs: {
      active: "Active",
      completed: "Completed",
      season: "Season",
    },
    completed_toast: "Mission completed!",
    claim: "Claim",
    no_completed: "No completed missions.",
    all_done: "All missions are complete!",
    level: "Level {{level}}",
  },

  // ── Mini-games ───────────────────────────────────
  mini_games: {
    title: "Mini-Games",
    rewards: "Rewards",
    modes: {
      training: "Training",
      competition: "Competition",
    },
    games: {
      reaction: "Reaction",
      batak: "Batak",
    },
    weekend: "Weekend: {{name}}",
    batak_title: "Batak Pro",
    score_shared: "Score shared in {{name}}!",
    share_error: "Error sharing score",
    // Grades (shared across games)
    grade_incredible: "INCREDIBLE!",
    grade_excellent: "Excellent!",
    grade_very_good: "Very good!",
    grade_good: "Good!",
    grade_ok: "Okay",
    grade_needs_work: "Needs work",
    // Shared actions
    training_label: "Training",
    start: "START",
    start_30s: "START (30s)",
    retry: "Retry",
    save: "Save",
    loading: "Loading...",
    targets_unit: "targets",
    // Batak-specific
    batak_instruction: "Click the targets as fast as you can!",
    batak_share_league: "Share in a league",
    batak_share_title: "Share my score",
    batak_share_message: "I scored {{score}} targets in Batak Pro! {{grade}} Who can do better?",
    batak_no_leagues: "No leagues joined",
    // Reaction-specific
    reaction_title: "Reaction time",
    reaction_idle: "Press START to launch the sequence",
    reaction_waiting: "Wait for the lights...",
    reaction_ready: "READY...",
    reaction_wait_lights_out: "Wait for lights out!",
    reaction_go: "GO!",
    reaction_click_now: "CLICK NOW",
    reaction_false_start: "FALSE START!",
    reaction_too_early: "Too early!",
    // Leaderboard
    leaderboard_title: "Leaderboard {{game}}",
    leaderboard_league_weekend: "League - This weekend",
    leaderboard_no_scores_weekend: "No scores recorded this weekend",
    leaderboard_global: "Global leaderboard (all time)",
    leaderboard_no_scores: "No scores recorded",
    // Reward info
    reward_competition: "Competition mode: 3 attempts per game per weekend",
    reward_winner: "Each game's league winner earns",
    reward_xp: "XP earned for every game played",
    reward_training: "Unlimited training mode to improve!",
  },

  // ── League list ───────────────────────────────────
  league_list: {
    no_leagues: "No leagues",
    no_leagues_desc: "Create or join a league below",
    active: "Active",
    activate: "Activate",
    share: "Share",
  },

  // ── League detail ────────────────────────────────
  league_detail: {
    not_found: "League not found",
    name_placeholder: "League name",
    edit_title: "Edit league",
    description_label: "League description",
    description_placeholder: "Describe your league in a few words... (optional)",
    add_description: "Add a description to your league",
    tabs: {
      ranking: "Ranking",
      members: "Members",
    },
    chat: "Chat",
    share_title: "Share",
    code_copied: "Code copied!",
    copy_error: "Unable to copy",
    share_text: 'Join my F1 league "{{name}}" on PRONOKIF!',
    name_empty: "Name cannot be empty",
    updated: "League updated!",
    update_error: "Error updating league",
    load_error: "Error loading league",
  },

  // ── League settings ──────────────────────────────
  league_settings: {
    creator_actions: "Creator actions",
    transfer: "Transfer",
    delete: "Delete",
    leave: "Leave league",
    transfer_modal: {
      title: "Transfer ownership",
      description: "Select the new owner of {{name}}",
      no_username: "No username",
      no_members: "No other members",
      loading: "Transferring...",
      success: "Ownership transferred!",
      error: "Error transferring ownership",
      select_error: "Select a new owner",
    },
    delete_modal: {
      title: "Delete league",
      confirm: "Are you sure you want to delete {{name}}?",
      warning:
        "This action is irreversible. All members will be removed and the league data will be lost.",
      member_warning: "{{count}} other member(s) will be removed.",
      loading: "Deleting...",
      success: "League has been deleted",
      error: "Error deleting league",
    },
    leave_modal: {
      title: "Leave league",
      confirm: "Are you sure you want to leave {{name}}?",
      loading: "Leaving...",
      button: "Leave",
      success: "You left the league",
      error: "Error leaving the league",
    },
  },

  // ── League chat ──────────────────────────────────
  league_chat: {
    title: "Chat",
    not_found: "League not found",
    not_found_desc: "This league doesn't exist or you no longer have access.",
    no_messages: "No messages",
    be_first: "Be the first to write!",
    just_now: "Just now",
    updated: "Messages updated",
    placeholder: "Write a message...",
    send_error: "Error sending message",
    member_one: "member",
    member_other: "members",
  },

  // ── Join league ──────────────────────────────────
  join_league: {
    loading: "Loading invitation...",
    invalid_link: "Invalid link",
    already_member: "Already a member!",
    go_dashboard: "Go to dashboard",
    invitation: "Invitation",
    invited_text: "You've been invited to join a league",
    join_cta: "Join league",
    joining: "Joining...",
    login_required: "You'll need to log in to join",
    joined: 'You joined "{{name}}"!',
    join_error: "Error joining league",
  },

  // ── Member profile ───────────────────────────────
  member_profile: {
    title: "Profile",
    not_found: "Profile not found",
    not_found_desc: "This player doesn't exist or is no longer active.",
    load_error: "Unable to load profile",
    you: "You",
    tabs: {
      stats: "Stats",
      predictions: "Predictions",
    },
    stats: {
      races: "Races",
      exact_winners: "Exact winners",
      exact_poles: "Exact poles",
      best_reaction: "Best reaction",
      best_batak: "Best Batak",
    },
    shared_leagues: "Shared leagues",
    recent_predictions: "Recent predictions",
    member_since: "Member since",
    points: "Points",
    validated: "Validated",
    winner_label: "Winner:",
    pole_label: "Pole:",
  },

  // ── Custom predictions ───────────────────────────
  custom_predictions: {
    title: "Custom Picks",
    create: "Create",
    gp_label: "Grand Prix",
    info_text:
      "Create fun predictions for your league! The creator sets the right answer after the race.",
    points_info: "+2 points per correct answer",
    my_predictions: "My predictions",
    league_predictions: "League predictions ({{count}})",
    no_predictions: "No predictions yet",
    be_first: "Be the first to create a prediction for your league!",
    types: {
      yes_no: "Yes/No",
      multiple: "Multiple choice",
      free_text: "Free text",
    },
    finished: "Finished",
    set_answer: "Set answer",
    answer_saved: "Answer saved!",
    answer_success: "Correct answer set! Points awarded.",
    answer_recorded: "Answer recorded!",
    error_generic: "Error",
    correct_answer: "Correct answer:",
    your_answer: "Your answer: {{answer}}",
    submit_answer: "Submit my answer",
    answer_pending: "Answer recorded - Awaiting result",
    your_answer_placeholder: "Your answer...",
    create_modal: {
      title: "Create a prediction",
      question: "Question",
      question_placeholder: "Ex: Who will finish ahead, Hamilton or Leclerc?",
      answer_type: "Answer type",
      types: {
        yes_no: "Yes/No",
        text: "Text",
        choice: "Choice",
      },
      options: "Options",
      multi_answer: "Multiple answers",
      option_placeholder: "Option {{index}}",
      add_option: "Add option",
      creating: "Creating...",
      create: "Create prediction",
      created: "Prediction created!",
      error_question: "Add a question",
      error_choices: "Add at least 2 choices",
    },
    yes: "Yes",
    no: "No",
    delete_title: "Delete predictions",
    delete_warning:
      "Your predictions for <1>{{race}}</1> will be permanently deleted. This action is irreversible.",
    deleting: "Deleting...",
    delete: "Delete",
  },

  // ── Driver comparison ────────────────────────────
  driver_comparison: {
    title: "Comparator",
    subtitle: "Compare the stats of 2 drivers",
    swap: "Swap",
    driver_placeholder: "Driver",
    select_two: "Select two different drivers to compare",
    f1_stats: "F1 Stats",
    world_titles: "World titles",
    victories: "Wins",
    podiums: "Podiums",
    poles: "Poles",
    fastest_laps: "Fastest laps",
    career_points: "Career points",
    grand_prix: "Grand Prix",
    efficiency: "Efficiency",
    win_rate: "Win rate",
    podium_rate: "Podium rate",
    pole_rate: "Pole rate",
    points_per_race: "Points/race",
    quick_verdict: "Quick verdict",
    titles: "Titles",
    wins: "Wins",
    vs: "VS",
    verdict: {
      dominant:
        "{{name}} leads in {{count}} out of 7 categories. Their experience and stats speak for themselves.",
      slight:
        "{{name}} leads in {{count}} out of 7 categories. Their statistical profile shows a clear advantage.",
      tied: "These two drivers are very close! The result of a duel would be unpredictable.",
    },
    aria_view_profile: "View {{name}}'s profile",
  },

  // ── Badges ───────────────────────────────────────
  badges: {
    title: "Badges",
    locked: "(locked)",
    names: {
      beginner: "Beginner",
      regular: "Regular",
      veteran: "Veteran",
      scorer: "Scorer",
      expert: "Expert",
      streak: "On a streak",
      unstoppable: "Unstoppable",
      champion: "Champion",
      legend: "Legend",
    },
    descriptions: {
      beginner: "First prediction",
      regular: "5 predictions",
      veteran: "20 predictions",
      scorer: "100 total points",
      expert: "500 total points",
      streak: "3 GPs in a row",
      unstoppable: "5 GPs in a row",
      champion: "Top 1 in a league",
      legend: "1000 total points",
    },
  },

  // ── Error boundary ───────────────────────────────
  error_boundary: {
    title: "Red flag — system error",
    subtitle: "On-track incident",
    description: "An unexpected error occurred. The tech team has been notified.",
    radio_label: "Team radio",
    radio_message: "We've got a technical issue, we're on it...",
    retry: "Retry",
    back_home: "Back to paddock",
  },

  // ── Network status ───────────────────────────────
  network: {
    offline: "Offline — data will sync when reconnected",
    online: "Connection restored",
  },

  // ── Circuit map ──────────────────────────────────
  circuit_map: {
    filters: {
      all: "All",
      turns: "Turns",
      drs: "DRS",
      sectors: "Sectors",
    },
    features: {
      start: "Start",
      turn: "Turn",
      drs_zone: "DRS Zone",
      sector: "Sector",
      zone: "Zone",
    },
    badges: {
      interactive: "Interactive",
      static: "Static",
    },
    stats: {
      hotspots: "Hotspots",
      points: "Points",
      zones: "Zones",
    },
    first_corner: "First corner",
    interactive_label: "Interactive map {{name}}",
    circuit_label: "Circuit {{name}}",
  },

  // ── Not found (404) ──────────────────────────────
  not_found_page: {
    banner: "Red flag — session interrupted",
    title: "Red flag",
    description: "Page not found. The session has been interrupted — get back on track!",
    radio_label: "Team radio",
    radio_message:
      "\"We've lost your position. Come back to the pits and we'll get you back on track.\"",
    radio_engineer: "— Race engineer",
    back_paddock: "Back to paddock",
    report_issue: "Report an issue",
  },

  // ── Reward celebration ───────────────────────────
  reward: {
    level: "Level {{level}}!",
    tap_continue: "Tap to continue",
  },

  // ── New results banner ───────────────────────────
  new_results: {
    title: "Results available!",
  },

  // ── Race detail hero ─────────────────────────────
  race_hero: {
    sprint_badge: "Sprint Weekend",
    countdown: {
      d: "d",
      h: "h",
      m: "m",
      s: "s",
    },
    status: {
      finished: "Finished",
      live: "Live",
      cancelled: "Race cancelled",
    },
    cta: {
      results: "See results",
      locked: "Predictions locked",
      edit: "Edit my predictions",
      predict: "Predict",
      follow_live: "Follow live results",
    },
  },

  // ── Social proof ─────────────────────────────────
  social_proof: {
    text: "players have already submitted their predictions",
  },

  // ── Driver card ──────────────────────────────────
  driver_card: {
    win_one: "win",
    win_other: "wins",
  },

  // ── Streak widget ────────────────────────────────
  streak: {
    title: "{{count}}-day streak | Record: {{record}}",
    days_short: "d",
  },

  // ── Verify email ────────────────────────────────
  verify_email: {
    loading: "Verifying...",
    success_title: "Email verified!",
    success_link: "Back to dashboard",
    error_title: "Error",
    error_link: "Back",
    invalid_link: "Invalid verification link",
    success_message: "Your email has been verified successfully!",
    invalid_or_used: "This link is invalid or has already been used",
  },

  // ── Driver detail ───────────────────────────────
  driver_detail: {
    not_found: "Driver not found",
    load_error: "Unable to load driver info",
    back_label: "Back",
    drivers_label: "Drivers",
    compare_label: "Compare",
    compare_tooltip: "Compare with another driver",
    nav_drivers_aria: "Driver navigation",
    prev_driver: "Previous driver",
    next_driver: "Next driver",
    follow: "Follow",
    followed: "Driver followed!",
    season: "Season {{year}}",
    ranking: "Ranking",
    podiums: "Podiums",
    best_result: "Best result",
    avg_points: "Average points",
    victories: "victories",
    strengths: "Strengths",
    speed: "Speed",
    overtaking: "Overtaking",
    consistency: "Consistency",
    tyre_mgmt: "Tyre management",
    qualifying: "Qualifying",
    f1_career: "F1 Career",
    titles: "Titles",
    wins: "Wins",
    poles: "Poles",
    fastest_laps: "Fastest laps",
    gp_entered: "GPs entered",
    predict_performance: "Predict their performance",
    predict_desc: "Earn points by anticipating their results",
    predict_cta: "Predict",
    team_contract: "Team & Contract",
    contract_until: "Contract until {{year}}",
    useful_info: "Useful info",
    did_you_know: "Did you know?",
    age: "Age",
    height: "Height",
    since: "Since",
    years_suffix: "YRS",
    recent_results: "Recent results",
    see_all: "See all",
    no_results: "No results available yet",
    position_suffix: "{{pos}}th",
    position_1: "1st",
    position_2: "2nd",
    position_3: "3rd",
  },

  // ── Points history ──────────────────────────────
  points_history: {
    title: "Points history",
    races_with_results: "{{count}} races with results",
    no_history: "No history available",
    no_history_desc: "Make predictions to see your history",
    pending: "Pending",
    pole: "Pole",
    top10_quali: "Top 10 Quali",
    winner: "Winner",
    top10_race: "Top 10 Race",
    sprint_quali: "Sprint Quali",
    sprint_race: "Sprint Race",
    bonus: "Bonus (SC, DNF, FL, T1)",
  },
};

export default en;
